import React, { useMemo, useEffect, useState } from 'react';
import { ImpactFactorTrendChart } from '@/features/journal/components/charts/ImpactFactorTrendChart';
import { get } from '@/features/shared/lib/api';

/**
 * ScholarOutput5YearChart
 * - Line chart for "5 Years Scholar's Resources Output".
 * - Uses shared ImpactFactorTrendChart to render multi-series lines.
 * - Props:
 *   - series: [{ label: string, color: string, data: [{year:number, value:number}] }]
 *     If omitted, renders fallback sample matching the design (SCI/Scopus/KCI from 2021~2025).
 *   - scholarId: scholar ID for API call
 */
export default function ScholarOutput5YearChart({ series: externalSeries = null, scholarId = null }) {
  // API에서 데이터 가져오기 (externalSeries가 없을 때만)
  const [outputData, setOutputData] = useState(null);
  const [outputLoading, setOutputLoading] = useState(false);
  const [outputErr, setOutputErr] = useState(null);

  useEffect(() => {
    // externalSeries가 제공되면 API 호출하지 않음
    if (externalSeries !== null) {
      return;
    }

    // scholarId가 없으면 API 호출하지 않음
    if (!scholarId) {
      return;
    }

    let alive = true;
    async function fetchOutput() {
      setOutputLoading(true);
      setOutputErr(null);
      try {
        const response = await get(`/rm/v1/researcher/${encodeURIComponent(scholarId)}/scholar_output_5year`);
        if (alive) {
          if (response?.status && response?.result) {
            setOutputData(response.result);
          } else {
            setOutputErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setOutputData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setOutputErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setOutputLoading(false);
        }
      }
    }
    fetchOutput();
    return () => { alive = false; };
  }, [externalSeries, scholarId]);

  // 데이터 변환: API 데이터를 series 형식으로 변환
  const data = useMemo(() => {
    if (Array.isArray(externalSeries) && externalSeries.length) return externalSeries;

    if (outputData && typeof outputData === 'object') {
      const mapSeries = (label, color, arr) => {
        const rows = Array.isArray(arr) ? arr : [];
        const data = rows
          .filter(d => d && typeof d.indicator_year === 'number' && d.indicator_year > 0 && typeof d.impact !== 'undefined' && d.impact !== null && !isNaN(Number(d.impact)))
          .map(d => ({ year: d.indicator_year, value: Number(d.impact) }))
          .sort((a, b) => a.year - b.year);
        if (!data.length) return null;
        return { label, color, data };
      };

      const sci = mapSeries('SCIs', '#B298F2', outputData.SCI);
      const scopus = mapSeries('Scopus', '#FFBE8D', outputData.Scopus);
      const kci = mapSeries('KCI', '#5FA3E0', outputData.KCI);
      const series = [sci, scopus, kci].filter(Boolean);
      if (series.length > 0) return series;
    }
    // 데이터가 없으면 null 반환
    return null;
  }, [externalSeries, outputData]);

  // 로딩 상태 처리
  if (externalSeries === null && outputLoading) {
    return (
      <div className="flex items-center justify-center min-h-[220px] text-[#8D9AAF]">
        Loading...
      </div>
    );
  }

  // 에러 상태 처리
  if (externalSeries === null && outputErr) {
    return (
      <div className="flex items-center justify-center min-h-[220px] text-[#F3652D] text-sm">
        {outputErr}
      </div>
    );
  }

  // 데이터가 없는 경우
  if (!data || (Array.isArray(data) && data.length === 0)) {
    return (
      <div className="flex items-center justify-center min-h-[220px] text-[#8D9AAF]">
        데이터가 없습니다.
      </div>
    );
  }

  return (
    <div className="min-h-[220px]">
      <ImpactFactorTrendChart series={data} showLegend={false} />
      <div className="mt-3 flex items-center justify-center gap-6 text-[13px] text-[#6C7687]">
        {data.map((s, idx) => (
          <div key={idx} className="flex items-center gap-2 font-semibold">
            <span className="inline-block w-[10px] h-[10px] rounded-full" style={{ background: s.color }} />
            <span>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
